<template>
  <aw-dropdown
    placement="bottom-start"
    :offset="[0, 6]"
    class="cursor-pointer inline-flex items-center"
  >
    <template v-slot:btn="{ toggle }">
      <div 
        @click="toggle" 
        class="select-none relative"
      >
        <input
          name="AwColorPicker__Input"
          type="text"
          v-model="previewInputColor" 
          @input="hexInputUpdated" 
          @blur="alpha ? blurInputHexa : blurInputHex" 
          v-bind="$attrs"
          class="AwColorPicker__Input"
        />
        <div 
          v-if="modelValue"
          :style="{ backgroundColor: color }" 
          class="AwColorPicker__Preview w-9 h-9 border-l border-gray-300"
        />
        <div 
          v-else
          class="AwColorPicker__Placeholder w-9 h-9 border-l border-gray-300 flex justify-center items-center"
        />
      </div>
    </template>
    <template v-slot:dropdown="{ opened, dropdown }">
      <div
        v-show="opened"
        :ref="dropdown"
        class="Dropdown Dropdown--Hover absolute rounded-md bg-white max-w-xs text-sm top-full right-0 shadow-md z-10 border min-w-min w-60"
      >
        <div class="p-4 w-full cursor-default">
          <div 
            class="w-full h-40" 
            :style="{ backgroundColor: canvasColor }"
          >
            <div 
              class="w-full h-full" 
              style="background-image:linear-gradient(90deg,#fff,rgba(204,154,129,0));"
            >
              <div 
                ref="canvas" 
                class="w-full h-full relative cursor-pointer" 
                style="background-image:linear-gradient(0deg,#000,rgba(204,154,129,0));" 
                @mousedown="mousedownCanvas"
              >
                <div 
                  ref="canvasCursor" 
                  class="h-4 w-4 border border-gray-200 rounded-full bg-white absolute -left-2 -top-2 pointer-events-none" 
                  style="box-shadow:2px 2px 2px 0 rgb(0 0 0 / 20%)"
                />
              </div>
            </div>
          </div>
          <div class="w-full flex flex-col items-center mt-2 no-hover">
            <div 
              ref="line" 
              class="w-full h-3 relative cursor-pointer rounded-md" 
              style="background-image:linear-gradient(90deg,red 0,#ff0 17%,#0f0 33%,#0ff 50%,#00f 67%,#f0f 83%,red)" 
              @mousedown="mousedownLine"
            >
              <div 
                ref="lineCursor" 
                class="h-4 w-4 border border-gray-200 rounded-full bg-white absolute -left-2 pointer-events-none" 
                style="top:-2px;box-shadow:2px 2px 2px 0 rgb(0 0 0 / 20%)"
              />
            </div>
            <div 
              v-if="alpha"
              ref="opacityLine" 
              class="w-full h-3 mt-2 relative cursor-pointer rounded-md" 
              style="background:url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAAJElEQVQYV2NctWrVfwYkEBYWxojMZ6SDAmT7QGx0K1EcRBsFAADeG/3M/HteAAAAAElFTkSuQmCC') repeat" 
              @mousedown="mousedownOpacity"
            >
              <div 
                class="w-full h-full relative rounded-md" 
                :style="{ background: opacityLineBackground }"
              >
                <div 
                  ref="opacityCursor" 
                  class="h-4 w-4 border border-gray-200 rounded-full bg-white absolute -left-2 pointer-events-none" 
                  style="top:-2px;box-shadow:2px 2px 2px 0 rgb(0 0 0 / 20%)"
                />
              </div>
            </div>
          </div>
          <div class="flex items-center mt-4 justify-between no-hover">
            <div 
              v-show="inputType === 'rgb'" 
              class="flex"
            >
              <div class="relative w-full h-8">
                <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-2 h-8">
                  R:
                </span>
                <input 
                  v-model="colorLazy.r" 
                  @input="inputUpdated" 
                  @blur="blurInputR" 
                  class="shadow appearance-none border rounded text-right w-14 h-8 px-2 text-grey-darker"
                />
              </div>
              <div class="mx-1 relative">
                <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-2 h-8">
                  G:
                </span>
                <input 
                  v-model="colorLazy.g" 
                  @input="inputUpdated" 
                  @blur="blurInputG" 
                  class="shadow appearance-none border rounded text-right w-14 h-8 px-2 text-grey-darker"
                />
              </div>
              <div class="mr-1 relative">
                <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-2 h-8">
                  B:
                </span>
                <input 
                  v-model="colorLazy.b" 
                  @input="inputUpdated" 
                  @blur="blurInputB" 
                  class="shadow appearance-none border rounded text-right w-14 h-8 px-2 text-grey-darker"
                />
              </div>
            </div>
            <div 
              v-show="inputType === 'hex'" 
              class="relative w-full h-8"
            >
              <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-3 h-8">
                HEX:
              </span>
              <input 
                v-model="colorLazy.hex" 
                @input="hexInputUpdated" 
                @blur="blurInputHex" 
                class="shadow appearance-none border rounded-md w-40 h-8 text-grey-darker px-3 text-right"
              />
            </div>
            <div 
              v-show="inputType === 'rgba'" 
              class="flex flex-wrap gap-1"
            >
              <div class="relative h-8">
                <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-2 h-8">
                  R:
                </span>
                <input 
                  v-model="colorLazy.r" 
                  @input="inputUpdated" 
                  @blur="blurInputR" 
                  class="shadow appearance-none border rounded text-right w-16 h-8 px-2 text-grey-darker"
                />
              </div>
              <div class="relative h-8">
                <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-2 h-8">
                  G:
                </span>
                <input 
                  v-model="colorLazy.g" 
                  @input="inputUpdated" 
                  @blur="blurInputG" 
                  class="shadow appearance-none border rounded text-right w-16 h-8 px-2 text-grey-darker"
                />
              </div>
              <div class="relative h-8">
                <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-2 h-8">
                  B:
                </span>
                <input 
                  v-model="colorLazy.b" 
                  @input="inputUpdated" 
                  @blur="blurInputB" 
                  class="shadow appearance-none border rounded text-right w-16 h-8 px-2 text-grey-darker"
                />
              </div>
              <div class="relative h-8">
                <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-2 h-8">
                  A:
                </span>
                <input 
                  v-model="colorLazy.a" 
                  @input="inputUpdated" 
                  @blur="blurInputA" 
                  class="shadow appearance-none border rounded text-right w-16 h-8 px-2 text-grey-darker"
                />
              </div>
            </div>
            <div 
              v-show="inputType === 'hexa'" 
              class="relative w-full h-8"
            >
              <span class="absolute w-10 text-gray-500 top-0 bottom-0 flex items-center pointer-events-none left-3 h-8">
                HEXA:
              </span>
              <input 
                v-model="colorLazy.hexa" 
                @input="hexInputUpdated" 
                @blur="blurInputHexa" 
                class="shadow appearance-none border rounded-md w-40 h-8 text-grey-darker px-3 text-right"
              />
            </div>
            <div 
              class=" text-gray-500 cursor-pointer" 
              @mousedown.prevent 
              @click.stop.prevent="toggleInputs"
            >
              <svg style="width:24px;height:24px" viewBox="0 0 24 24">
                <path fill="currentColor" d="M12,18.17L8.83,15L7.42,16.41L12,21L16.59,16.41L15.17,15M12,5.83L15.17,9L16.58,7.59L12,3L7.41,7.59L8.83,9L12,5.83Z" />
              </svg>
            </div>
          </div>
          <div 
            v-if="!hideSwatches" 
            class="grid mt-4 gap-1 grid-cols-6 no-hover w-full"
          >
            <div
              v-for="(swatch, i) in swatchesLazy" 
              :key="i" 
              class="w-8 h-8 relative cursor-pointer rounded-full shadow-md col-span-1" 
              :class="{'border-2 border-gray-200' : inputValue === swatch}" 
              :style="{ backgroundColor: swatch }" 
              @click="selectSwatch(swatch)"
            />
          </div>
        </div>
      </div>
    </template>
  </aw-dropdown>
</template>

<script>
export default {
  inheritAttrs: false,
  emits: [
    'addSwatch',
    'deleteSwatch', 
    'input', 
    'update:modelValue',
    'update:swatches', 
  ],
  props: {
    alpha: {
      type: Boolean,
      default: false
    },
    hideSwatches: Boolean,
    modelValue: {
      type: String,
      default: null
    },
    swatches: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      canvasCursor: null,
      lineCursor: null,
      opacityCursor: null,
      draggingLineCursor: false,
      draggingCanvasCursor: false,
      draggingOpacityCursor: false,
      dragStartColor: null,
      lineWidth: 216,
      lineLeft: 0,
      canvasWidth: 208,
      canvasLeft: 0,
      canvasTop: 0,
      canvasHeight: 0,
      opacityWidth: 216,
      opacityLeft: 0,
      percentageOpacity: 0,
      percentageBlack: 0,
      percentageWhite: 0,
      inputType: this.alpha ? 'hexa' : 'hex',
      colorLazy: {
        r: null,
        g: null,
        b: null,
        a: null,
        hexa: null,
        hex: null,
      },
      colorData: {
        r: null,
        g: null,
        b: null,
        a: null,
        hexa: null,
        hex: null,
      },
      lineColorData: {
        r: 0,
        g: 0,
        b: 0
      },
      swatchesLazy: []
    }
  },
  computed: {
    previewInputColor: {
      get() {
        if (!this.alpha) {
          return this.colorLazy.hex
        }

        return this.colorLazy.hexa
      },
      set(val) {
        if (!this.alpha) {
          this.colorLazy.hex = val
        } else {
          this.colorLazy.hexa = val
        }
      }
    },
    inputValue: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    swatchesValue: {
      get() {
        return this.swatches || []
      },
      set(val) {
        this.$emit('update:swatches', val)
      }
    },
    hasSelectedSwatch() {
      return this.swatchesLazy.includes(this.inputValue)
    },
    color() {
      if (!this.alpha) {
        return `rgb(${this.colorData.r}, ${this.colorData.g}, ${this.colorData.b})`
      }

      return `rgba(${this.colorData.r}, ${this.colorData.g}, ${this.colorData.b}, ${this.colorData.a})`
    },
    canvasColor() {
      if (!this.alpha) {
        return `rgb(${this.lineColorData.r}, ${this.lineColorData.g}, ${this.lineColorData.b})`
      }

      return `rgba(${this.lineColorData.r}, ${this.lineColorData.g}, ${this.lineColorData.b}, 1)`
    },
    opacityLineBackground() {
      if (!this.alpha) {
        return
      }

      let opaque = `rgba(${this.colorData.r}, ${this.colorData.g}, ${this.colorData.b}, 0)`
      let solid = `rgba(${this.colorData.r}, ${this.colorData.g}, ${this.colorData.b}, 1)`
      return `linear-gradient(to right, ${opaque}, ${solid})`
    }
  },
  methods: {
    mousedownCanvas(e) {
      if (e.which !== 1) {
        return
      }
      this.registerListeners()
      this.dragStartColor = this.color
      this.draggingCanvasCursor = true
      this.setSizePoses()
      this.canvasCursor = this.$refs.canvasCursor
      this.canvasCursor.style.transform = `translate(${e.offsetX}px, ${e.offsetY}px)`
      this.percentageBlack = e.offsetY / this.canvasHeight
      this.percentageWhite = 1 - e.offsetX / this.canvasWidth
      this.setColorData()
      e.stopPropagation()
      e.preventDefault()
    },
    mousedownLine(e) {
      this.lineWidth = this.$refs.line.offsetWidth

      if (e.which !== 1) {
        return
      }
      this.registerListeners()

      this.dragStartColor = this.color
      this.draggingLineCursor = true
      this.setSizePoses()
      this.lineCursor = this.$refs.lineCursor
      this.lineCursor.style.transform = `translate(${e.offsetX}px, 0px)`
      this.calculateLineColor(e.offsetX)
      this.setColorData()
      e.stopPropagation()
      e.preventDefault()
    },
    mousedownOpacity(e) {
      this.opacityWidth = this.$refs.opacityLine.offsetWidth
      
      if (!this.alpha || e.which !== 1) {
        return
      }
      this.registerListeners()

      this.dragStartColor = this.color
      this.draggingOpacityCursor = true
      this.setSizePoses()
      this.opacityCursor = this.$refs.opacityCursor
      this.opacityCursor.style.transform = `translate(${e.offsetX}px, 0px)`
      this.percentageOpacity = e.offsetX / this.opacityWidth

      this.setColorData()
      e.stopPropagation()
      e.preventDefault()
    },
    mouseup() {
      if (this.draggingLineCursor || this.draggingCanvasCursor || this.draggingOpacityCursor) {
        if (this.dragStartColor !== this.color) {
          this.$emit('update:modelValue', this.colorData.hex)
        }
      }
      this.draggingLineCursor = false
      this.draggingCanvasCursor = false

      if (this.alpha) {
        this.draggingOpacityCursor = false
      }

      this.unregisterListeners()
    },
    mousemove(e) {
      let pos

      if (this.draggingLineCursor) {
        pos = e.clientX - this.lineLeft
        pos = Math.min(this.lineWidth, Math.max(0, pos))
        this.lineCursor.style.transform = `translate(${pos}px, 0px)`
        this.calculateLineColor(pos)
      } else if (this.draggingCanvasCursor) {
        let posX = e.clientX - this.canvasLeft
        let posY = e.clientY - this.canvasTop
        posX = Math.min(this.canvasWidth, Math.max(0, posX))
        posY = Math.min(this.canvasHeight, Math.max(0, posY))
        this.canvasCursor.style.transform = `translate(${posX}px, ${posY}px)`
        this.percentageBlack = posY / this.canvasHeight
        this.percentageWhite = 1 - posX / this.canvasWidth
      } else if (this.draggingOpacityCursor && this.alpha) {
        pos = e.clientX
        pos = e.clientX - this.opacityLeft
        pos = Math.min(this.opacityWidth, Math.max(0, pos))
        this.opacityCursor.style.transform = `translate(${pos}px, 0px)`
        this.percentageOpacity = pos / this.opacityWidth
      }
      this.setColorData()
    },
    setColorData() {
      let targetVal = 255 * (1 - this.percentageBlack)
      targetVal = Math.min(255, Math.max(0, Math.round(targetVal)))

      let remainingR = targetVal - this.lineColorData.r
      let remainingG = targetVal - this.lineColorData.g
      let remainingB = targetVal - this.lineColorData.b

      let rDiff = this.percentageWhite * remainingR
      let gDiff = this.percentageWhite * remainingG
      let bDiff = this.percentageWhite * remainingB

      let r = this.lineColorData.r + rDiff
      let g = this.lineColorData.g + gDiff
      let b = this.lineColorData.b + bDiff

      this.colorData.r = Math.min(targetVal, Math.max(0, Math.round(r)))
      this.colorData.g = Math.min(targetVal, Math.max(0, Math.round(g)))
      this.colorData.b = Math.min(targetVal, Math.max(0, Math.round(b)))

      if (this.alpha) {
        this.colorData.a = Math.min(1, Math.max(0, Number(this.percentageOpacity.toFixed(2))))
      }

      this.colorLazy.r = this.colorData.r
      this.colorLazy.g = this.colorData.g
      this.colorLazy.b = this.colorData.b

      if (this.alpha) {
        this.colorLazy.a = this.colorData.a
      }

      if (!this.alpha) {
        this.colorData.hex =
          '#' +
          this.componentToHex(this.colorData.r) +
          this.componentToHex(this.colorData.g) +
          this.componentToHex(this.colorData.b)
        this.colorLazy.hex = this.colorData.hex
        this.inputValue = this.colorData.hex
      } else {
        this.colorData.hexa =
          '#' +
          this.componentToHex(this.colorData.r) +
          this.componentToHex(this.colorData.g) +
          this.componentToHex(this.colorData.b) +
          this.componentToHex(Math.round(this.colorData.a * 255))
        this.colorLazy.hexa = this.colorData.hexa
        this.inputValue = this.colorData.hexa
      }
    },
    calculateLineColor(linePos) {
      let perc = linePos / this.lineWidth
      let value = perc % (1 / 6)
      let colorPerc = value / (1 / 6)
      let percRed = 1
      let percGreen = 1
      let percBlue = 1

      if (perc < 1 / 6) {
        percGreen = colorPerc
        percBlue = 0
      } else if (perc < 2 / 6) {
        percRed = 1 - colorPerc
        percBlue = 0
      } else if (perc < 3 / 6) {
        percRed = 0
        percBlue = colorPerc
      } else if (perc < 4 / 6) {
        percRed = 0
        percGreen = 1 - colorPerc
      } else if (perc < 5 / 6) {
        percRed = colorPerc
        percGreen = 0
      } else {
        percGreen = 0
        percBlue = 1 - colorPerc
      }
      
      this.lineColorData.r = Math.min(255, Math.max(0, Math.round(percRed * 255)))
      this.lineColorData.g = Math.min(255, Math.max(0, Math.round(percGreen * 255)))
      this.lineColorData.b = Math.min(255, Math.max(0, Math.round(percBlue * 255)))
    },
    registerListeners() {
      document.addEventListener('mouseup', this.mouseup)
      document.addEventListener('mousemove', this.mousemove)
    },
    unregisterListeners() {
      document.removeEventListener('mouseup', this.mouseup)
      document.removeEventListener('mousemove', this.mousemove)
    },
    setSizePoses() {
      let boundingRect = this.$refs.line.getBoundingClientRect()
      this.lineLeft = boundingRect.left

      let canvBoundingRect = this.$refs.canvas.getBoundingClientRect()
      this.canvasLeft = canvBoundingRect.left
      this.canvasTop = canvBoundingRect.top
      this.canvasHeight = canvBoundingRect.height

      if (this.alpha) {
        boundingRect = this.$refs.opacityLine.getBoundingClientRect()
        this.opacityLeft = boundingRect.left
      }
    },
    normalizeColorData() {
      if (!this.colorData.r || !this.colorData.g || !this.colorData.b) {
        return null
      }

      let normalized = {
        r: null,
        g: null,
        b: null,
        variable: null
      }
      let { r, g, b } = this.colorData
      if (b > g && b > r) {
        normalized.b = 1
      } else if (g > r && g > b) {
        normalized.g = 1
      } else {
        normalized.r = 1
      }
      if (r < g && r < b) {
        normalized.r = 0
      } else if (g < r && g < b) {
        normalized.g = 0
      } else {
        normalized.b = 0
      }
      Array.from('rgb').forEach((col) => {
        if (normalized[col] === null) {
          normalized.variable = col
          normalized[col] = this.colorData[col] / 255
        }
      })

      return normalized
    },
    initSwatches() {
      let cleanedSwatches
      if (this.swatches !== null && Array.isArray(this.swatches)) {
        cleanedSwatches = this.swatches.map((s) => this.alpha ? this.cleanHexa(s) : this.cleanHex(s))
        cleanedSwatches.forEach((swatch) => {
          if (!this.swatchesLazy.includes(swatch)) {
            this.swatchesLazy.push(swatch)
          }
        })
      } else {
        cleanedSwatches = [
          '#f94144',
          '#f3722c',
          '#f8961e',
          '#f9c74f',
          '#90be6d',
          '#43aa8b',
          '#577590',
          '#22223b',
          '#4a4e69',
          '#c9ada7'
        ].map((s) => this.alpha ? this.cleanHexa(s) : this.cleanHex(s))
        this.swatchesLazy = cleanedSwatches
      }
    },
    init() {
      if (this.modelValue) {
        this.colorLazy = this.alpha ? this.parseHexa(this.modelValue) : this.parseHex(this.modelValue)
        this.validate()
      }
      if (!this.hideSwatches) this.initSwatches()
      this.setUICursors()
    },
    inputUpdated() {
      this.validate()
      this.setUICursors()
    },
    hexInputUpdated() {
      this.validateHex()
      this.setUICursors()
      this.$emit('update:modelValue', this.colorData.hex)
    },
    validateHexChar(c) {
      if (c.length < 0 || c.length > 1) return '0'
      if (isNaN(c)) {
        let validChars = ['A', 'B', 'C', 'D', 'E', 'F']
        if (validChars.includes(c.toUpperCase())) {
          return c.toUpperCase()
        } else {
          return '0'
        }
      } else {
        return c
      }
    },
    cleanHex(hex) {
      // Check if hex is invalid (null, undefined, or not 6 characters)
      if (!hex || hex.length < 6) return null

      // Clean the hex string (convert to uppercase and remove # if present)
      let _cleaned = hex.toUpperCase()
      if (_cleaned.startsWith('#')) _cleaned = _cleaned.substr(1)

      // Return null if length is not exactly 6
      if (_cleaned.length !== 6) return null

      // Validate if all characters are valid hex chars
      if (!/^[0-9A-F]+$/.test(_cleaned)) return null

      let r, g, b
      r = g = b = null

      // Extract RGB values from the 6-character hex string
      r = _cleaned[0] + _cleaned[1]
      g = _cleaned[2] + _cleaned[3]
      b = _cleaned[4] + _cleaned[5]

      return `#${r}${g}${b}`
    },
    cleanHexa(hexa) {
      if (!hexa || hexa.length < 8) return null

      let _cleaned = hexa.toUpperCase()
      if (_cleaned.startsWith('#')) _cleaned = _cleaned.substr(1)
      if (_cleaned.length !== 8) return null
      if (!/^[0-9A-F]+$/.test(_cleaned)) return null

      const r = _cleaned[0] + _cleaned[1]
      const g = _cleaned[2] + _cleaned[3]
      const b = _cleaned[4] + _cleaned[5]
      const a = _cleaned[6] + _cleaned[7]

      return `#${r}${g}${b}${a}`
    },
    parseHex(hex) {
      const cleanedHex = this.cleanHex(hex)
      if (!cleanedHex) {
        return null
      }

      let hexArr = cleanedHex.substr(1).split('')

      let h = hexArr[0] + hexArr[1]
      let e = hexArr[2] + hexArr[3]
      let x = hexArr[4] + hexArr[5]
      let r = parseInt(h, 16)
      let g = parseInt(e, 16)
      let b = parseInt(x, 16)

      return {
        hex: '#' + h + e + x,
        r,
        g,
        b,
      }
    },
    parseHexa(hexa) {
      let hexaArr = this.cleanHexa(hexa).substr(1).split('')

      let h = hexaArr[0] + hexaArr[1]
      let e = hexaArr[2] + hexaArr[3]
      let x = hexaArr[4] + hexaArr[5]
      let ha = hexaArr[6] + hexaArr[7]
      let r = parseInt(h, 16)
      let g = parseInt(e, 16)
      let b = parseInt(x, 16)
      let a = parseInt(ha, 16) / 255

      return {
        hexa: '#' + h + e + x + ha,
        r,
        g,
        b,
        a
      }
    },
    validateHex() {
      let parsedColor = this.alpha ? this.parseHexa(this.colorLazy.hexa) : this.parseHex(this.colorLazy.hex)
      this.colorData = { ...parsedColor }

      this.colorLazy.r = this.colorData.r
      this.colorLazy.g = this.colorData.g
      this.colorLazy.b = this.colorData.b
      if (this.alpha) {
        this.colorLazy.a = this.colorData.a
      }
    },
    componentToHex(c) {
      let hex = c.toString(16).toUpperCase()
      return hex.length == 1 ? '0' + hex : hex
    },
    validate() {
      let r = Number(this.colorLazy.r)
      let g = Number(this.colorLazy.g)
      let b = Number(this.colorLazy.b)

      let a = null
      if (this.alpha) {
        a = Number(this.colorLazy.a)
      }

      if (isNaN(r) || r === null) r = 0
      if (isNaN(g) || g === null) g = 0
      if (isNaN(b) || b === null) b = 0

      if (this.alpha) {
        if (isNaN(a) || a === null) a = 1
      }

      r = Math.min(255, Math.max(0, r))
      g = Math.min(255, Math.max(0, g))
      b = Math.min(255, Math.max(0, b))

      if (this.alpha) {
        a = Math.min(1, Math.max(0, a))
      }

      this.colorData.r = r
      this.colorData.g = g
      this.colorData.b = b

      if (!this.alpha) {
        this.colorData.hex =
          '#' +
          this.componentToHex(r) +
          this.componentToHex(g) +
          this.componentToHex(b)
        this.colorLazy.hex = this.colorData.hex
      } else {
        this.colorData.a = a
        this.colorData.hexa =
          '#' +
          this.componentToHex(r) +
          this.componentToHex(g) +
          this.componentToHex(b) +
          this.componentToHex(Math.round(a * 255))
        this.colorLazy.hexa = this.colorData.hexa
      }
    },
    setUICursors() {
      this.setSizePoses()

      if (this.alpha) {
        this.percentageOpacity = this.colorData.a
        let opacityX = this.percentageOpacity * this.opacityWidth
        this.$refs.opacityCursor.style.transform = `translate(${opacityX}px, 0px)`
      }

      let normalized = this.normalizeColorData()

      if (!normalized) {
        return null
      }

      let sector = 0
      let variablePerc = 0
      if (normalized.variable === 'r') {
        if (normalized.g === 1) {
          sector = 1
          variablePerc = 1 - this.colorData.r / 255
        } else if (normalized.b === 1) {
          sector = 4
          variablePerc = this.colorData.r / 255
        }
      } else if (normalized.variable === 'b') {
        if (normalized.r === 1) {
          sector = 5
          variablePerc = 1 - this.colorData.b / 255
        } else if (normalized.g === 1) {
          sector = 2
          variablePerc = this.colorData.b / 255
        }
      } else {
        if (normalized.r === 1) {
          sector = 0
          variablePerc = this.colorData.g / 255
        } else if (normalized.b === 1) {
          sector = 3
          variablePerc = 1 - this.colorData.g / 255
        }
      }
      let sectorLength = this.lineWidth / 6
      let variableSectorLeft = variablePerc * sectorLength

      let lineCursorLeft = sectorLength * sector + variableSectorLeft
      this.$refs.lineCursor.style.transform = `translate(${lineCursorLeft}px, 0px)`

      this.lineColorData.r = Math.min(255, Math.max(0, Math.round(normalized.r * 255)))
      this.lineColorData.g = Math.min(255, Math.max(0, Math.round(normalized.g * 255)))
      this.lineColorData.b = Math.min(255, Math.max(0, Math.round(normalized.b * 255)))

      Array.from('rgb').forEach((col) => {
        if (this.lineColorData[col] === 0) {
          this.percentageWhite = this.colorData[col] / 255
        } else if (this.lineColorData[col] === 255) {
          this.percentageBlack = 1 - this.colorData[col] / 255
        }
      })

      let canvCursorX = this.canvasWidth * (1 - this.percentageWhite)
      let canvCursorY = this.canvasHeight * this.percentageBlack
      this.$refs.canvasCursor.style.transform = `translate(${canvCursorX}px, ${canvCursorY}px)`
    },
    blurInputR() {
      this.colorLazy.r = this.colorData.r
    },
    blurInputG() {
      this.colorLazy.g = this.colorData.g
    },
    blurInputB() {
      this.colorLazy.b = this.colorData.b
    },
    blurInputA() {
      this.colorLazy.a = this.colorData.a
    },
    blurInputHex() {
      this.colorLazy.hex = this.colorData.hex
    },
    blurInputHexa() {
      this.colorLazy.hexa = this.colorData.hexa
    },
    toggleInputs() {
      if (!this.alpha) {
        if (this.inputType === 'rgb') {
          this.inputType = 'hex'
        } else {
          this.inputType = 'rgb'
        }
      } else {
        if (this.inputType === 'rgba') {
          this.inputType = 'hexa'
        } else {
          this.inputType = 'rgba'
        }
      }
    },
    selectSwatch(swatchHexa) {
      let parsedHexa = this.alpha ? this.parseHexa(swatchHexa) : this.parseHex(swatchHexa)
      this.colorData = { ...parsedHexa }
      this.colorLazy = { ...parsedHexa }
      this.inputValue = this.alpha ? parsedHexa.hexa : parsedHexa.hex
      this.$emit('update:modelValue', this.colorData.hex)
      this.setUICursors()
    },
    deleteSwatch(swatch) {
      this.swatchesLazy = this.swatchesLazy.filter((s) => s !== swatch)
      this.$emit('update:swatches', this.swatchesLazy)
      this.$emit('deleteSwatch', swatch)
    },
    addRemoveCurrentSwatch() {
      if (this.hasSelectedSwatch) {
        this.deleteSwatch(this.inputValue)
      } else {
        this.swatchesLazy.push(this.inputValue)
        this.$emit('update:swatches', this.swatchesLazy)
        this.$emit('addSwatch', this.inputValue)
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.init()
    })
  },
  beforeUnmount() {
    this.unregisterListeners()
  },
  watch: {
    modelValue(val) {
      if (val == '') {
        this.colorLazy.r = null
        this.colorLazy.g = null
        this.colorLazy.b = null
        this.colorLazy.a = null
        this.colorLazy.hexa = null
        this.colorLazy.hex = null
      
        this.colorData.r = null
        this.colorData.g = null
        this.colorData.b = null
        this.colorData.a = null
        this.colorData.hexa = null
        this.colorData.hex = null

        this.lineColorData.r = 0
        this.lineColorData.g = 0
        this.lineColorData.b = 0
        this.previewInputColor = null
      } else {
        this.previewInputColor = val
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.AwColorPicker__Preview,
.AwColorPicker__Placeholder {
  position: absolute;
  top: 1px;
  right: 1px;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  height: calc(100% - 2px);
}

.AwColorPicker__Placeholder {
  background-color: rgb(255, 255, 255);
  background-image:
    linear-gradient(45deg, rgb(230, 230, 230) 25%, transparent 25%, transparent 75%, rgb(230, 230, 230) 75%, rgb(230, 230, 230)),
    linear-gradient(45deg, rgb(230, 230, 230) 25%, transparent 25%, transparent 75%, rgb(230, 230, 230) 75%, rgb(230, 230, 230));
  background-size: 12px 12px;
  background-position: 0 0, 6px 6px;
}
</style>
